/**
 * Timezone Conversion Utilities
 *
 * IMPORTANT: These functions are for TIMESTAMP fields only (createdDate, stateDate, etc.)
 * For DATE-ONLY fields (availableDate, dueDate, workDate), use dateService.js instead.
 *
 * @see /src/services/dateService.js for the new standardized approach
 */

import moment from 'moment-timezone';
import dateService from '../services/dateService';

// Get user's timezone (cached in dateService)
const getTzName = () => dateService.getUserTimezone();

/**
 * Convert a local date/time to UTC for sending to API
 * Use this for TIMESTAMP fields only (createdDate, stateDate, quoteSentAt, etc.)
 *
 * @param {Date|moment|string} date - Local date/time
 * @returns {moment} Moment object in UTC
 *
 * @example
 * // Sending timestamp to API
 * stateDate: convertToUTC(new Date()).format("YYYY-MM-DD HH:mm:ss")
 */
const convertToUTC = (date) => {
  if (!date) return null;
  const localTime = moment.tz(date, getTzName());
  return localTime.clone().tz("UTC");
};

/**
 * Convert a UTC date/time from API to local timezone for display
 * Use this for TIMESTAMP fields only (createdDate, stateDate, quoteSentAt, etc.)
 *
 * @param {string} date - UTC date/time string from API
 * @returns {moment} Moment object in user's local timezone
 *
 * @example
 * // Displaying timestamp from API
 * convertFromUTC(request.createdDate).format("MMM D, YYYY h:mm A")
 */
const convertFromUTC = (date) => {
  if (!date) return null;
  const UTCTime = moment.tz(date, "UTC");
  return UTCTime.clone().tz(getTzName());
};

/**
 * Get the user's timezone name
 * @returns {string} IANA timezone name (e.g., "America/New_York")
 */
const getUserTimezone = () => getTzName();

/**
 * Get the user's timezone abbreviation
 * @returns {string} Timezone abbreviation (e.g., "EST", "PST")
 */
const getTimezoneAbbr = () => dateService.getTimezoneAbbr();

export {
  convertFromUTC,
  convertToUTC,
  getUserTimezone,
  getTimezoneAbbr
};